/**
 * Normalização tolerante do ENVELOPE das respostas do backend do warehouse.
 *
 * O contrato do M0 fixa rotas, métodos e chaves snake_case, mas não congela o
 * envelope das listagens (array puro × `{items,...}` × `{operators,...}` com
 * paging). Como o backend é construído em paralelo, o frontend aceita as
 * formas de envelope usuais do projeto. Isso NÃO é fallback de contrato wire
 * (as chaves de dado continuam exclusivamente snake_case).
 */

/** Extrai as linhas de uma resposta de listagem. */
export function ExtractListRows(payload, preferredKeys = []) {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (!payload || typeof payload !== 'object') {
        return [];
    }
    // `results` primeiro: é o envelope REAL do backend do M0 (verificado por
    // curl na integração). Os demais ficam por tolerância, e o fallback de
    // "primeiro array do objeto" cobre envelope futuro — mas depender dele para
    // o caso conhecido seria frágil: bastaria o payload ganhar outro array
    // antes de `results` para a listagem trocar de fonte em silêncio.
    for (const key of [...preferredKeys, 'results', 'items', 'rows']) {
        if (Array.isArray(payload[key])) {
            return payload[key];
        }
    }
    const firstArray = Object.values(payload).find((value) => Array.isArray(value));
    return firstArray || [];
}

function toFiniteNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

/**
 * Prefixo do RACK como o wire o entrega: LETRA(s) em caixa alta (`A`, `AB`),
 * ou `null` quando vazio/ausente. Cada porta-pallet é um rack independente
 * identificado por esse prefixo — é STRING, nunca passa por Number().
 */
export function NormalizeRack(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const text = String(value).trim().toUpperCase();
    return text ? text : null;
}

// Contagens por status de um nó do mapa. O contrato do M1 fixa a SEMÂNTICA
// ("resumo por rack/bloco com contagens por status") mas não o shape das
// contagens — aceita tanto chaves diretas (active/blocked/maintenance,
// maiúsculas ou _count) quanto um objeto aninhado (counts/status_counts/by_status).
function extractStatusCounts(node) {
    const nested = [node.counts, node.status_counts, node.by_status].find(
        (value) => value && typeof value === 'object' && !Array.isArray(value),
    );
    const source = nested || node;
    const active = toFiniteNumber(source.ACTIVE ?? source.active ?? source.active_count);
    const blocked = toFiniteNumber(source.BLOCKED ?? source.blocked ?? source.blocked_count);
    const maintenance = toFiniteNumber(source.MAINTENANCE ?? source.maintenance ?? source.maintenance_count);
    const total = toFiniteNumber(node.total ?? node.total_count ?? node.positions ?? node.count);
    return {
        active: active ?? 0,
        blocked: blocked ?? 0,
        maintenance: maintenance ?? 0,
        total: total ?? (active ?? 0) + (blocked ?? 0) + (maintenance ?? 0),
    };
}

/**
 * Normaliza GET /warehouse/locations/map — `{orientation, racks: [{rack: 'A',
 * bays: [{bay, total, active, blocked, maintenance}]}]}` — para linhas planas
 * `[{rack: 'A', bay: 1, total, active, blocked, maintenance}]` (só blocos do
 * rack — bay >= 1; a cabeceira `STG-A-00` fica de fora do grid). Aceita também
 * a linha já plana `{rack, bay, ...}`. Rack vazio/nulo é ignorado; `rack` é a
 * LETRA do porta-pallet (string) e `bay` o nº sequencial do bloco (inteiro).
 */
export function ParseLocationsMapRows(payload) {
    const rows = ExtractListRows(payload, ['racks', 'map']);
    const flat = [];
    for (const row of rows) {
        if (!row || typeof row !== 'object') {
            continue;
        }
        const rack = NormalizeRack(row.rack);
        if (rack === null) {
            continue;
        }
        const nestedBays = Array.isArray(row.bays) ? row.bays : null;
        if (nestedBays) {
            for (const bayRow of nestedBays) {
                if (!bayRow || typeof bayRow !== 'object') {
                    continue;
                }
                const bay = toFiniteNumber(bayRow.bay);
                if (bay === null || bay < 1) {
                    continue;
                }
                flat.push({ rack, bay, ...extractStatusCounts(bayRow) });
            }
        } else {
            const bay = toFiniteNumber(row.bay);
            if (bay === null || bay < 1) {
                continue;
            }
            flat.push({ rack, bay, ...extractStatusCounts(row) });
        }
    }
    return flat;
}

/**
 * Normaliza GET /warehouse/settings para `[{key, value}]`.
 * Aceita lista de `{key, value}` (forma esperada) ou dicionário chave→valor.
 */
export function ParseSettingsEntries(payload) {
    // Sem `results` aqui, o envelope real do backend caía no ramo de dicionário
    // e a aba Parâmetros mostrava UMA linha chamada "results" com um array JSON
    // dentro — achado da verificação de integração do M0.
    const source =
        payload && typeof payload === 'object' && !Array.isArray(payload)
            ? (payload.results ?? payload.settings ?? payload.items ?? payload)
            : payload;

    if (Array.isArray(source)) {
        return source
            .filter((entry) => entry && typeof entry === 'object' && typeof entry.key === 'string')
            .map((entry) => ({ key: entry.key, value: entry.value, label: entry.label ?? null }));
    }

    if (source && typeof source === 'object') {
        return Object.entries(source).map(([key, value]) => ({ key, value }));
    }

    return [];
}
